import { FC, MouseEvent, useEffect, useState } from 'react'
import { Typography, Button, Popover } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { Dayjs } from 'dayjs'
import {
  ActionButtons,
  StyledDatePickerWrapper,
  StyledInput,
  DateRangeWrapper,
  StyledDateRangeCalendar,
  popoverSx
} from './dateRangePicker.styles'
import { CalendarIcon } from '../svg'
import theme from 'theme'

interface Props {
  value?: [Dayjs | null, Dayjs | null]
  onChange?: (value: [Dayjs | null, Dayjs | null]) => void
  label?: string
  disableFuture?: boolean
}

const DateRangePickerComponent: FC<Props> = ({ value, onChange, disableFuture, label = 'Time Period' }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>(
    value || [dayjs().startOf('month'), dayjs().endOf('month')]
  )
  const [tempDateRange, setTempDateRange] = useState<[Dayjs | null, Dayjs | null]>(dateRange)

  useEffect(() => {
    if (value && value[0] && value[1]) {
      setDateRange(value)
    }
  }, [value?.[0]?.valueOf(), value?.[1]?.valueOf()])

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
    setTempDateRange(dateRange)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleOk = () => {
    setDateRange(tempDateRange)
    onChange?.(tempDateRange)
    handleClose()
  }

  const handleCancel = () => {
    setTempDateRange(dateRange)
    handleClose()
  }

  const formatDateRange = () => {
    if (dateRange[0] && dateRange[1]) {
      return `${dateRange[0].format('MM/DD/YYYY')} - ${dateRange[1].format('MM/DD/YYYY')}`
    }
    return 'Select date range'
  }

  const open = Boolean(anchorEl)

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DateRangeWrapper className="date-range-wrapper">
        {label && <Typography className="date-range-wrapper--label">{label}</Typography>}

        <StyledInput onClick={handleClick}>
          <CalendarIcon />
          <Typography className="date-range-input-text">{formatDateRange()}</Typography>
        </StyledInput>

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
          sx={popoverSx}
        >
          <StyledDatePickerWrapper>
            <StyledDateRangeCalendar
              value={tempDateRange}
              onChange={(newValue: any) => setTempDateRange(newValue)}
              calendars={1}
              disableFuture={disableFuture}
            />

            <ActionButtons>
              <Button
                onClick={handleCancel}
                sx={{
                  color: theme.colors.menuItemText,
                  textTransform: 'none',

                  '&:hover': {
                    backgroundColor: theme.colors.calendarActionHover
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleOk}
                sx={{
                  color: theme.colors.cta,
                  textTransform: 'none',
                  marginRight: `${theme.spacing.md}px`,

                  '&:hover': {
                    backgroundColor: 'rgba(124, 58, 237, 0.1)'
                  }
                }}
              >
                Ok
              </Button>
            </ActionButtons>
          </StyledDatePickerWrapper>
        </Popover>
      </DateRangeWrapper>
    </LocalizationProvider>
  )
}

export default DateRangePickerComponent
